import { observable } from 'mobx';
import moment from 'moment';
import type { Moment } from '../../lib/moment';

export type RoomElementResponseArgs = {
  id: number;
  name: string;
  address?: string;
  remark?: string;
  started_at?: string;
  ended_at?: string;
  reservation_number?: string;
};

export type RoomElementArgs = {
  id: number;
  name: string;
  address?: string;
  remark?: string;
  startedAt?: Moment;
  endedAt?: Moment;
  reservationNumber?: string;
};

export const convertRoomElementResponseToArgs = (response: RoomElementResponseArgs) => {
  const args: RoomElementArgs = {
    id: response.id,
    name: response.name,
    address: response.address,
    remark: response.remark,
    startedAt: response.started_at ? moment(response.started_at) : undefined,
    endedAt: response.ended_at ? moment(response.ended_at) : undefined,
    reservationNumber: response.reservation_number
  };
  return args;
};

export default class RoomElement {
  @observable id: number;
  @observable name: string;
  @observable address?: string;
  @observable remark?: string;
  @observable startedAt?: Moment;
  @observable endedAt?: Moment;
  @observable reservationNumber?: string;

  constructor(args: RoomElementArgs) {
    this.id = args.id;
    this.name = args.name;
    this.address = args.address || '';
    this.remark = args.remark || '';
    this.startedAt = args.startedAt;
    this.endedAt = args.endedAt;
    this.reservationNumber = args.reservationNumber || '';
  }

  get type() {
    return 'room';
  }

  get title() {
    return '会議室';
  }

  get summary() {
    return `[会議室]${this.name}`;
  }

  detail() {
    return '';
  }

  validationErrors(orderItemStatus?: number): string[] {
    const errors: string[] = [];
    const needStatusGuard = orderItemStatus === 7 || orderItemStatus === 13;

    if (!this.name || this.name === '') {
      errors.push('会議室の施設名を入力してください。');
    }

    if (this.endedAt && this.startedAt && this.endedAt < this.startedAt) {
      errors.push('利用開始以降を指定してください。');
    }

    if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
      errors.push('予約番号を入力してください。');
    }

    return errors;
  }

  get updateParams() {
    return {
      type: this.type,
      started_at: this.startedAt?.format('YYYY-MM-DD HH:mm:ss'),
      ended_at: this.endedAt?.format('YYYY-MM-DD HH:mm:ss'),
      name: this.name,
      address: this.address,
      remark: this.remark,
      reservation_number: this.reservationNumber
    };
  }

  get description(): string {
    return `\
<会議室>
■施設名：${this.name}
■住所：${this.address}
■ご利用日時：${this.startedAt?.format('MM/DD HH:mm')}〜${this.endedAt?.format('MM/DD HH:mm')}
■備考
${this.remark}\
`;
  }

  get startTime() {
    return this.startedAt;
  }

  get endTime() {
    return this.endedAt;
  }
}
